import { Component, ElementRef, Inject, OnDestroy, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import * as moment from 'moment';
import { BaseControl } from '../../../shared/toolbar/controls/base-control';
import { ToolbarControlContext, TOOLBAR_CONTROL_CONTEXT } from '../../../shared/contracts/toolbar-control.tokens';
import { ToolbarDrawerService } from '../../../shared/toolbar/toolbar-drawer.service';
import { ApiService } from 'src/app/services/api.service';
import { PopupService } from 'src/app/services/popup.service';
import { CollectionPopupComponent } from 'src/app/components/shared';
import { DataService } from 'src/app/services/data.service';
import { ThemeService } from 'src/app/services/theme.service';
import { VesselFinderMapController, VesselFinderVesselRow } from '../../vesselfinder-map.controller';
import { actionCredits } from 'src/app/services/operators/vesselfinder-credit-actions';
import {
  applyVesselFinderCreditSpend,
  hasVesselFinderToiFinished,
  isVesselFinderToiNotStarted,
  VESSEL_FINDER_TOI_ENDED_MESSAGE,
  VESSEL_FINDER_TOI_NOT_STARTED_MESSAGE,
  vesselFinderCreditsRemaining,
  vesselFinderSpendBlocker,
} from '../../vf-credits.model';
import { VF_DEFAULT_INTERVAL_MINUTES, VF_DEFAULT_LIMIT, vfTodayUtc } from './vf-form-defaults';

/**
 * VesselFinder's Expected Arrivals control — same UI/interaction shape as
 * `VfSearchControlComponent` (`sp-*` classes, same paid-primary-button
 * treatment, same click-a-row-to-locate act), standing next to it on the
 * bar right after Port Calls.
 *
 * WHERE IT DIFFERS: this searches by PORT (a LOCODE) within a time window,
 * not by vessel identifier — so the box is a small filter form (locode,
 * interval OR a from/to date pair, limit, AIS type) instead of one text
 * input, and results are never merged into the free "on the map" list
 * (there is no local filter to offer here — a port query has no smaller,
 * free-to-browse subset the way an MMSI/IMO search's already-located hulls
 * do). Clicking a result row still centers the map and opens its popup,
 * exactly like `VfSearchControlComponent.pick()`.
 */
@Component({
  selector: 'app-vf-expected-arrivals-control',
  template: `
    <button #trigger type="button" class="tb-btn"
            [class.tb-btn--active]="drawer.isOpen(panel)"
            (click)="onToggleDrawer(trigger)"
            aria-label="Expected Arrivals"
            [matTooltip]="'Expected arrivals · ' + costLabel() + '/vessel'"
            matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">directions_boat</mat-icon>
    </button>

    <ng-template #panel>
      <app-toolbar-drawer [title]="showingAisTypes ? 'AIS Vessel Type Codes' : 'Expected arrivals'"
                          [subtitle]="showingAisTypes ? null : (costLabel() + '/vessel')"
                          (close)="onCloseDrawer()">
        <div class="sp-panel" [class.is-dark]="isDark">

          <!-- ══ AIS Type Codes View ══ -->
          <ng-container *ngIf="showingAisTypes">
            <div class="ea-results-bar">
              <button type="button" class="ea-nav-btn" (click)="showingAisTypes = false" matTooltip="Return to search form">
                <mat-icon class="material-icons-outlined">arrow_back</mat-icon>
                <span>Back to search</span>
              </button>
            </div>

            <div class="ais-types-inline-container">
              <table class="ais-types-table">
                <thead>
                  <tr>
                    <th class="col-code">Type Code</th>
                    <th class="col-desc">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of aisTypesList">
                    <td class="col-code">{{ item.code }}</td>
                    <td class="col-desc">{{ item.description }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ng-container>

          <!-- ══ Results View ══ -->
          <ng-container *ngIf="!showingAisTypes && showingResults && results.length">
            <div class="ea-results-bar">
              <button type="button" class="ea-nav-btn" (click)="backToSearch()" matTooltip="Return to search form">
                <mat-icon class="material-icons-outlined">arrow_back</mat-icon>
                <span>Back</span>
              </button>
              <button type="button" class="ea-nav-btn ea-nav-btn--map" (click)="viewAllOnMap()" matTooltip="Show all vessels on map and minimize drawer">
                <mat-icon class="material-icons-outlined">map</mat-icon>
                <span>View on Map</span>
              </button>
            </div>

            <div class="sp-sl sp-sl--paid">
              Results
              <span class="sp-sl__n">{{ results.length }}</span>
            </div>
            <div class="sp-list">
              <button type="button" class="sp-row" *ngFor="let r of results"
                      [class.sp-row--selected]="isSelected(r)"
                      (click)="pick(r, $event)">
                <span class="sp-row__gl">◆</span>
                <span class="sp-row__main">
                  <span class="sp-row__nm">{{ r.name || identifierLabel(r) }}</span>
                  <span class="sp-row__meta">{{ fullMeta(r) }}</span>
                  <span class="sp-row__meta sp-row__meta--soft">{{ compactDetails(r) }}</span>
                </span>
              </button>
            </div>
          </ng-container>

          <!-- ══ Search Form View ══ -->
          <ng-container *ngIf="!showingAisTypes && (!showingResults || !results.length)">
            <button type="button" class="ea-results-link" *ngIf="results.length" (click)="showActiveResults()">
              <mat-icon class="material-icons-outlined">view_list</mat-icon>
              <span>View search results ({{ results.length }})</span>
              <mat-icon class="material-icons-outlined">chevron_right</mat-icon>
            </button>

            <label class="ea-field">
              <span class="ea-field__label">
                LOCODE
                <span class="ea-field__req">*</span>
                <mat-icon class="material-icons-outlined ea-field__info"
                          matTooltip="UN/LOCODE of the destination port, for example BGVAR."
                          matTooltipPosition="below"
                          (mouseenter)="$event.stopPropagation()"
                          aria-label="About LOCODE">info</mat-icon>
              </span>
              <input class="ea-field__input" type="text" placeholder="e.g. BGVAR"
                     [(ngModel)]="locode" (ngModelChange)="onFieldChange()" />
            </label>

            <label class="ea-field">
              <span class="ea-field__label">
                Interval (minutes)
                <span class="ea-field__req">*</span>
                <mat-icon class="material-icons-outlined ea-field__info"
                          matTooltip="Searches arrivals in a rolling window ending now. Example: 1440 = last 24 hours."
                          matTooltipPosition="below"
                          (mouseenter)="$event.stopPropagation()"
                          aria-label="About interval">info</mat-icon>
              </span>
              <input class="ea-field__input" type="number" min="1" placeholder="e.g. 1440"
                     [(ngModel)]="intervalMinutes" (ngModelChange)="onFieldChange()" />
            </label>

            <div class="ea-row2">
              <label class="ea-field">
                <span class="ea-field__label">
                  From date (UTC)
                  <span class="ea-field__req">*</span>
                  <mat-icon class="material-icons-outlined ea-field__info"
                            matTooltip="Start date in UTC."
                            matTooltipPosition="below"
                            (mouseenter)="$event.stopPropagation()"
                            aria-label="About from date">info</mat-icon>
                </span>
                <input class="ea-field__input" type="date" [(ngModel)]="fromDate" (ngModelChange)="onFromDateChange()" />
              </label>
              <label class="ea-field">
                <span class="ea-field__label">
                  To date (UTC)
                  <span class="ea-field__req">*</span>
                  <mat-icon class="material-icons-outlined ea-field__info"
                            matTooltip="End date in UTC."
                            matTooltipPosition="below"
                            (mouseenter)="$event.stopPropagation()"
                            aria-label="About to date">info</mat-icon>
                </span>
                <input class="ea-field__input" type="date" [(ngModel)]="toDate" [min]="minToDate" (ngModelChange)="onFieldChange()" />
              </label>
            </div>

            <div class="ea-row2">
              <label class="ea-field">
                <span class="ea-field__label">
                  Limit
                  <mat-icon class="material-icons-outlined ea-field__info"
                            matTooltip="Maximum number of vessels to return for this search."
                            matTooltipPosition="below"
                            (mouseenter)="$event.stopPropagation()"
                            aria-label="About limit">info</mat-icon>
                </span>
                <input class="ea-field__input" type="number" min="1" [max]="maxLimit" [placeholder]="'up to ' + maxLimit"
                       [(ngModel)]="limit" (ngModelChange)="onFieldChange()" />
              </label>
              <label class="ea-field">
                <span class="ea-field__label">
                  AIS type(s)
                  <mat-icon class="material-icons-outlined ea-field__info"
                            matTooltip="Optional vessel type codes, comma-separated, for example 70,71."
                            matTooltipPosition="below"
                            (mouseenter)="$event.stopPropagation()"
                            aria-label="About AIS types">info</mat-icon>
                  <mat-icon class="material-icons-outlined ea-field__info ea-field__doc"
                            matTooltip="View AIS Vessel Type Codes"
                            matTooltipPosition="below"
                            (click)="openAisTypesView($event)"
                            aria-label="View AIS type codes">description</mat-icon>
                </span>
                <input class="ea-field__input" type="text" placeholder="e.g. 70,71"
                       [(ngModel)]="aisTypeRaw" (ngModelChange)="onFieldChange()" />
              </label>
            </div>

            <div class="sp-error" *ngIf="error">{{ error }}</div>

            <button type="button" class="tb-drawer__primary tb-drawer__primary--follows vf-cta"
                    [disabled]="loading || !canSearch() || !!searchBlocker()"
                    [matTooltip]="searchBlocker() || 'Search arrivals expected at this port'"
                    (click)="search()">
              <mat-icon class="material-icons-outlined" [class.sp-spin]="loading">public</mat-icon>
              <span class="tb-drawer__primary-label">{{ loading ? 'Searching…' : 'Search arrivals' }}</span>
            </button>
            <div class="tb-drawer__cost">
              <ng-container *ngIf="searchBlocker() as why; else haveCredits">{{ why }}</ng-container>
              <ng-template #haveCredits>{{ costNote }}</ng-template>
            </div>
          </ng-container>

        </div>
      </app-toolbar-drawer>
    </ng-template>
  `,
  styles: [`
    .sp-panel { width: 300px; max-width: 100%; }

    .ea-field { display: flex; flex-direction: column; gap: 2px; margin-bottom: 8px; }
    .ea-field__label {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 10.5px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      opacity: 0.72;
    }
    .ea-field__req {
      color: #ef4444;
      font-weight: 700;
      font-size: 11px;
      line-height: 1;
    }
    .ea-field__info {
      width: 13px;
      height: 13px;
      line-height: 13px;
      font-size: 13px;
      opacity: 0.8;
      cursor: help;
    }
    .ea-field__doc {
      cursor: pointer;
      color: #2563eb;
      opacity: 0.9;
    }
    .ea-field__doc:hover {
      opacity: 1;
    }

    .ais-types-inline-container {
      max-height: 320px;
      overflow-y: auto;
      border: 1px solid rgba(128, 128, 128, 0.25);
      border-radius: 8px;
      margin-top: 4px;
      background: #ffffff;
    }
    .sp-panel.is-dark .ais-types-inline-container {
      background: #12161c !important;
      border-color: rgba(255, 255, 255, 0.15) !important;
    }

    .ais-types-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 11.5px;
    }
    .ais-types-table th {
      text-align: left;
      padding: 7px 10px;
      border-bottom: 2px solid rgba(128, 128, 128, 0.3);
      font-size: 10.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      position: sticky;
      top: 0;
      background: #f8fafc;
      color: #475569;
      z-index: 2;
    }
    .sp-panel.is-dark .ais-types-table th {
      background: #181e26 !important;
      color: #9aa4b2 !important;
      border-bottom: 2px solid rgba(255, 255, 255, 0.2) !important;
    }

    .ais-types-table td {
      padding: 6px 10px;
      border-bottom: 1px solid rgba(128, 128, 128, 0.15);
      color: inherit;
    }
    .sp-panel.is-dark .ais-types-table td {
      border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
      color: #e2e8f0 !important;
    }

    .ais-types-table tbody tr:hover {
      background: rgba(37, 99, 235, 0.08);
    }
    .sp-panel.is-dark .ais-types-table tbody tr:hover {
      background: rgba(63, 154, 255, 0.15) !important;
    }

    .col-code {
      width: 70px;
      font-weight: 600;
      white-space: nowrap;
    }
    .col-desc {
      line-height: 1.35;
    }

    .ea-field__input {
      font: inherit;
      font-size: 12.5px;
      padding: 5px 8px;
      border: 1px solid rgba(128, 128, 128, 0.4);
      border-radius: 8px;
      background: transparent;
      color: inherit;
      outline: none;
    }
    .ea-field__input:focus { border-color: #2563EB; }

    /* Two attempts in this file asked the browser to recolour its own calendar
       glyph -- a filter on the pseudo-element, then color-scheme -- and neither
       showed it on the dark panel. So stop asking. appearance: none drops the
       native paint entirely and the background-image below is OURS, one per
       theme, in the same muted inks the rest of this panel uses. What gets
       drawn is now what is written here, with no native rendering in the way.

       The explicit box matters: without a width and height the indicator
       collapses once its native appearance is gone, taking the click target
       with it.

       color-scheme stays, but only for the dropdown calendar it gives dark
       chrome to -- it is no longer load-bearing for the icon.

       Written with :host-context() rather than a .dark-theme descendant
       selector: the class sits on <body>, outside this component's
       emulated-encapsulation scope, where a plain descendant selector is
       never rewritten to match and the rule is simply dead. */
    .ea-field__input[type="date"]::-webkit-calendar-picker-indicator {
      -webkit-appearance: none;
      appearance: none;
      width: 14px;
      height: 14px;
      margin: 0 0 0 4px;
      padding: 0;
      cursor: pointer;
      opacity: 0.8;
      background-color: transparent;
      background-repeat: no-repeat;
      background-position: center;
      background-size: 14px 14px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='5' width='18' height='16' rx='2'/%3E%3Cline x1='16' y1='3' x2='16' y2='7'/%3E%3Cline x1='8' y1='3' x2='8' y2='7'/%3E%3Cline x1='3' y1='11' x2='21' y2='11'/%3E%3C/svg%3E");
    }
    .ea-field__input[type="date"]::-webkit-calendar-picker-indicator:hover {
      opacity: 1;
    }
    :host-context(.dark-theme) .ea-field__input[type="date"] {
      color-scheme: dark;
    }
    :host-context(.dark-theme) .ea-field__input[type="date"]::-webkit-calendar-picker-indicator {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239aa4b2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='5' width='18' height='16' rx='2'/%3E%3Cline x1='16' y1='3' x2='16' y2='7'/%3E%3Cline x1='8' y1='3' x2='8' y2='7'/%3E%3Cline x1='3' y1='11' x2='21' y2='11'/%3E%3C/svg%3E");
    }

    .ea-row2 { display: flex; gap: 8px; }
    .ea-row2 .ea-field { flex: 1 1 0; min-width: 0; }
    .ea-hint { font-size: 10.5px; opacity: 0.6; margin: -4px 0 8px; }

    .ea-results-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(128, 128, 128, 0.2);
    }
    .ea-nav-btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      border: 1px solid rgba(128, 128, 128, 0.35);
      border-radius: 6px;
      background: transparent;
      color: inherit;
      font: inherit;
      font-size: 11.5px;
      font-weight: 500;
      padding: 4px 8px;
      cursor: pointer;
    }
    .ea-nav-btn:hover {
      background: rgba(37, 99, 235, 0.08);
      border-color: #2563EB;
      color: #2563EB;
    }
    .ea-nav-btn--map {
      background: #2563EB;
      color: #fff;
      border-color: #2563EB;
    }
    .ea-nav-btn--map:hover {
      background: #1d4ed8;
      color: #fff;
    }
    .ea-nav-btn .mat-icon {
      font-size: 15px;
      width: 15px;
      height: 15px;
      line-height: 15px;
    }

    .ea-results-link {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 6px;
      width: 100%;
      padding: 6px 8px;
      margin-bottom: 10px;
      border: 1px solid rgba(37, 99, 235, 0.4);
      border-radius: 8px;
      background: rgba(37, 99, 235, 0.06);
      color: #2563EB;
      font: inherit;
      font-size: 11.5px;
      font-weight: 600;
      cursor: pointer;
      text-align: left;
    }
    .ea-results-link:hover {
      background: rgba(37, 99, 235, 0.12);
    }
    .ea-results-link .mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      line-height: 16px;
    }

    .sp-sl {
      display: flex;
      align-items: baseline;
      gap: 6px;
      padding: 9px 2px 4px;
      font-size: 10.5px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      opacity: 0.6;
    }
    .sp-sl__n { font-weight: 600; font-variant-numeric: tabular-nums; opacity: 0.85; }
    .sp-sl--paid { color: #2563EB; opacity: 0.9; }

    .sp-list { display: flex; flex-direction: column; max-height: 208px; overflow-y: auto; }

    .sp-row {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 6px 4px;
      font: inherit;
      text-align: left;
      border: none;
      background: transparent;
      color: inherit;
      cursor: pointer;
      border-radius: 6px;
    }
    .sp-row + .sp-row { border-top: 1px solid rgba(128, 128, 128, 0.18); }
    .sp-row:hover { background: rgba(37, 99, 235, 0.08); }
    .sp-row:focus-visible { outline: 2px solid #2563EB; outline-offset: -2px; }
    .sp-row--selected {
      background: rgba(37, 99, 235, 0.16) !important;
      border: 1px solid rgba(37, 99, 235, 0.5) !important;
    }
    .sp-row--selected .sp-row__nm {
      color: #2563EB;
      font-weight: 700;
    }
    .sp-row--selected .sp-row__gl {
      color: #2563EB;
      opacity: 1;
    }
    .sp-panel.is-dark .sp-row--selected {
      background: rgba(59, 130, 246, 0.22) !important;
      border-color: rgba(96, 165, 250, 0.5) !important;
    }
    .sp-panel.is-dark .sp-row--selected .sp-row__nm {
      color: #60a5fa !important;
    }
    .sp-row__gl { flex: 0 0 auto; font-size: 10px; opacity: 0.55; width: 10px; }
    .sp-row__main { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; line-height: 1.25; }
    .sp-row__nm { font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .sp-row__meta { font-size: 10.5px; opacity: 0.72; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .sp-row__meta--soft { opacity: 0.62; }

    .sp-error { margin-top: 8px; padding: 7px 8px; border-radius: 6px; background: rgba(217, 119, 6, 0.12); font-size: 11px; line-height: 1.45; }


    /*
     * These two CTAs dropped their cost badge — the price is on the drawer
     * subtitle and spelled out in the cost line below, so a third copy on the
     * button was noise. With the badge gone, the drawer's shared rule for
     * .tb-drawer__primary-label (flex: 1; text-align: left) would strand the
     * label hard left against a lone icon, so icon and label are centred as a
     * pair instead.
     *
     * Scoped to .vf-cta, and doubled on .tb-drawer__primary deliberately: the
     * shared rule carries three classes, which ties on specificity with a
     * plainer local selector and would then be decided by stylesheet order.
     * Other drawers still carry their cost badge and keep the left-aligned
     * original.
     *
     * NOTE: no backticks in here — this comment lives inside the styles
     * template literal, and one would close the string.
     */
    .tb-drawer__primary.vf-cta { justify-content: center; }
    .tb-drawer__primary.vf-cta .tb-drawer__primary-label { flex: 0 0 auto; text-align: center; }

    .sp-spin { animation: sp-spin 1.1s linear infinite; }
    @keyframes sp-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) { .sp-spin { animation: none; } }
  `],
})
export class VfExpectedArrivalsControlComponent extends BaseControl implements OnDestroy {
  @ViewChild('panel') panel!: TemplateRef<any>;
  @ViewChild('trigger') triggerRef!: ElementRef;

  locode = '';
  intervalMinutes: number | null = VF_DEFAULT_INTERVAL_MINUTES;
  fromDate = vfTodayUtc();
  toDate = vfTodayUtc();
  limit: number | null = VF_DEFAULT_LIMIT;
  aisTypeRaw = '';
  loading = false;
  error: string | null = null;
  results: VesselFinderVesselRow[] = [];
  selectedKey: string | null = null;
  showingResults = false;
  showingAisTypes = false;
  private _themeSub?: Subscription;

  readonly maxLimit = 100;
  /**
   * `vesselFinderExpectedArrivalsLimit` — what the SERVER requests when the
   * field is left blank, which is not `VF_DEFAULT_LIMIT` (what the field opens
   * on) and not `maxLimit` either. The worst-case guard below used `maxLimit`
   * for the blank case, so it priced a cleared field at 100 vessels while the
   * server would ask for 25 — refusing searches the balance could afford, and
   * it would now have printed that wrong number in the cost line too.
   */
  readonly blankLimit = 25;

  readonly aisTypesList: Array<{ code: string; description: string }> = [
    { code: '0', description: 'Not available (default)' },
    { code: '1-19', description: 'Reserved for future use' },
    { code: '20', description: 'Wing in ground (WIG), all ships of this type' },
    { code: '21', description: 'Wing in ground (WIG), Hazardous category A' },
    { code: '22', description: 'Wing in ground (WIG), Hazardous category B' },
    { code: '23', description: 'Wing in ground (WIG), Hazardous category C' },
    { code: '24', description: 'Wing in ground (WIG), Hazardous category D' },
    { code: '25-29', description: 'Wing in ground (WIG), Reserved for future use' },
    { code: '30', description: 'Fishing' },
    { code: '31', description: 'Towing' },
    { code: '32', description: 'Towing: length exceeds 200m or breadth exceeds 25m' },
    { code: '33', description: 'Dredging or underwater ops' },
    { code: '34', description: 'Diving ops' },
    { code: '35', description: 'Military ops' },
    { code: '36', description: 'Sailing' },
    { code: '37', description: 'Pleasure Craft' },
    { code: '38-39', description: 'Reserved' },
    { code: '40', description: 'High speed craft (HSC), all ships of this type' },
    { code: '41', description: 'High speed craft (HSC), Hazardous category A' },
    { code: '42', description: 'High speed craft (HSC), Hazardous category B' },
    { code: '43', description: 'High speed craft (HSC), Hazardous category C' },
    { code: '44', description: 'High speed craft (HSC), Hazardous category D' },
    { code: '45-48', description: 'High speed craft (HSC), Reserved for future use' },
    { code: '49', description: 'High speed craft (HSC), No additional information' },
    { code: '50', description: 'Pilot Vessel' },
    { code: '51', description: 'Search and Rescue vessel' },
    { code: '52', description: 'Tug' },
    { code: '53', description: 'Port Tender' },
    { code: '54', description: 'Anti-pollution equipment' },
    { code: '55', description: 'Law Enforcement' },
    { code: '56-57', description: 'Spare - Local Vessel' },
    { code: '58', description: 'Medical Transport' },
    { code: '59', description: 'Noncombatant ship according to RR Resolution No. 18' },
    { code: '60', description: 'Passenger, all ships of this type' },
    { code: '61', description: 'Passenger, Hazardous category A' },
    { code: '62', description: 'Passenger, Hazardous category B' },
    { code: '63', description: 'Passenger, Hazardous category C' },
    { code: '64', description: 'Passenger, Hazardous category D' },
    { code: '65-68', description: 'Passenger, Reserved for future use' },
    { code: '69', description: 'Passenger, No additional information' },
    { code: '70', description: 'Cargo, all ships of this type' },
    { code: '71', description: 'Cargo, Hazardous category A' },
    { code: '72', description: 'Cargo, Hazardous category B' },
    { code: '73', description: 'Cargo, Hazardous category C' },
    { code: '74', description: 'Cargo, Hazardous category D' },
    { code: '75-78', description: 'Cargo, Reserved for future use' },
    { code: '79', description: 'Cargo, No additional information' },
    { code: '80', description: 'Tanker, all ships of this type' },
    { code: '81', description: 'Tanker, Hazardous category A' },
    { code: '82', description: 'Tanker, Hazardous category B' },
    { code: '83', description: 'Tanker, Hazardous category C' },
    { code: '84', description: 'Tanker, Hazardous category D' },
    { code: '85-88', description: 'Tanker, Reserved for future use' },
    { code: '89', description: 'Tanker, No additional information' },
    { code: '90', description: 'Other Type, all ships of this type' },
    { code: '91', description: 'Other Type, Hazardous category A' },
    { code: '92', description: 'Other Type, Hazardous category B' },
    { code: '93', description: 'Other Type, Hazardous category C' },
    { code: '94', description: 'Other Type, Hazardous category D' },
    { code: '95-98', description: 'Other Type, Reserved for future use' },
    { code: '99', description: 'Other Type, no additional information' },
  ];

  get isDark(): boolean {
    return (
      document.body.classList.contains('dark-theme') ||
      document.body.classList.contains('dark') ||
      !!(this.themeService as any)?.isDarkMode?.()
    );
  }

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    private readonly api: ApiService,
    private readonly popupService: PopupService,
    public vfCtrl: VesselFinderMapController,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
    private readonly dialog: MatDialog,
    private readonly dataService: DataService,
    private readonly themeService: ThemeService,
  ) {
    super();
  }

  ngOnDestroy(): void {
    this._themeSub?.unsubscribe();
  }

  /** Back to the opening state — the shipped defaults, not an empty form. */
  clear(): void {
    this.locode = '';
    this.intervalMinutes = VF_DEFAULT_INTERVAL_MINUTES;
    this.fromDate = vfTodayUtc();
    this.toDate = vfTodayUtc();
    this.limit = VF_DEFAULT_LIMIT;
    this.aisTypeRaw = '';
    this.error = null;
    this.results = [];
    this.selectedKey = null;
    this.showingResults = false;
    this.showingAisTypes = false;
  }

  isSelected(r: VesselFinderVesselRow): boolean {
    const key = String(r.mmsi ?? r.imo ?? '');
    return !!key && key === this.selectedKey;
  }

  onToggleDrawer(trigger: any): void {
    this.drawer.toggle(this.panel, this.vcr, trigger);
  }

  onCloseDrawer(): void {
    this.error = null;
    this.drawer.close();
  }

  openAisTypesView(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.showingAisTypes = true;
    this.showingResults = false;
  }

  backToSearch(): void {
    this.showingResults = false;
    this.showingAisTypes = false;
  }

  showActiveResults(): void {
    if (this.results.length) {
      this.showingResults = true;
    }
  }

  viewAllOnMap(): void {
    if (!this.results.length) return;
    this.vfCtrl.setSearchResults(this.results);
    this.drawer.close();
    const validWithFix = this.results.filter(
      (r) =>
        r.latitude != null &&
        r.longitude != null &&
        Number.isFinite(Number(r.latitude)) &&
        Number.isFinite(Number(r.longitude)),
    );
    if (validWithFix.length) {
      const keys = validWithFix.map((r) => String(r.mmsi ?? r.imo ?? '')).filter((k) => !!k);
      setTimeout(() => {
        if (keys[0]) {
          this.vfCtrl.centerVessel(keys[0]);
        }
        this.vfCtrl.activateAll(keys);
      }, 50);
    }
  }

  private get sensor(): any {
    return this.context.options?.['sensor'];
  }

  /** Null while the spend may proceed, a human-readable refusal otherwise. */
  searchBlocker(): string | null {
    return vesselFinderSpendBlocker(this.sensor, 'expectedArrivals');
  }

  /** Vessels this search will ask for — the typed limit, clamped, or the server's blank fallback. */
  private get requestedVessels(): number {
    return this.limit != null ? Math.min(this.limit, this.maxLimit) : this.blankLimit;
  }

  /**
   * The cost line under the button — the same sentence Port Calls prints,
   * in this endpoint's own units (a vessel matched, not a port call returned).
   * Both drawers price per unit returned with the limit as the only dial, so
   * the two lines say the same thing about the same bargain and should read
   * alike.
   */
  get costNote(): string {
    const unit = actionCredits(this.sensor, 'expectedArrivals') || 0;
    const n = this.requestedVessels;
    const total = n * unit;
    return (
      `${unit} ${unit === 1 ? 'credit' : 'credits'} per vessel returned — ` +
      `up to ${total} ${total === 1 ? 'credit' : 'credits'} for ${n} ${n === 1 ? 'vessel' : 'vessels'}.`
    );
  }

  costLabel(): string {
    const c = actionCredits(this.sensor, 'expectedArrivals');
    return `${c} ${c === 1 ? 'credit' : 'credits'}`;
  }

  identifierLabel(r: VesselFinderVesselRow): string {
    return r.mmsi ? `MMSI ${r.mmsi}` : r.imo ? `IMO ${r.imo}` : '—';
  }

  /** "MMSI 638014251 · IMO 9345678 · → BGVAR" — identifiers plus the AIS destination this search matched on. */
  fullMeta(r: VesselFinderVesselRow): string {
    const parts: string[] = [];
    if (r.mmsi) parts.push(`MMSI ${r.mmsi}`);
    if (r.imo) parts.push(`IMO ${r.imo}`);
    return parts.length ? parts.join(' · ') : '—';
  }

  get minToDate(): string | null {
    return this.fromDate ? this.fromDate : null;
  }

  onFromDateChange(): void {
    if (this.fromDate && this.toDate && this.toDate < this.fromDate) {
      this.toDate = '';
    }
    this.onFieldChange();
  }

  compactDetails(r: VesselFinderVesselRow): string {
    const parts: string[] = [];
    if (r.destination) parts.push(`Destination: ${r.destination}`);
    if (r.lastPort) parts.push(`Last port: ${r.lastPort}`);
    return parts.length ? parts.join(' · ') : 'No destination or last port available';
  }

  canSearch(): boolean {
    return (
      !this._validationError() &&
      !!this.locode.trim() &&
      !!this.intervalMinutes &&
      !!this.fromDate &&
      !!this.toDate
    );
  }

  /**
   * Every refusal the form can make on its own, in the order a user meets
   * them — one definition shared by `canSearch`, `onFieldChange` and
   * `search()`, which each carried their own copy before and had already
   * drifted: all three checked the limit's ceiling and none its floor, so a
   * limit of 0 or -5 passed the form and went to the server.
   *
   * Same shape and same wording as `VfPortCallControlComponent`: these are
   * sibling drawers over sibling endpoints, and a user who meets "Limit must
   * be between 1 and 100" in one should meet the same sentence in the other.
   */
  private _validationError(): string | null {
    if (this.limit != null && (this.limit < 1 || this.limit > this.maxLimit)) {
      return `Limit must be between 1 and ${this.maxLimit}.`;
    }
    if (this.intervalMinutes != null && this.intervalMinutes < 1) {
      return 'Interval must be at least 1 minute.';
    }
    if (this.fromDate && this.toDate && this.toDate < this.fromDate) {
      return 'To date cannot be earlier than From date.';
    }
    return null;
  }

  onFieldChange(): void {
    this.error = this._validationError();
  }

  /**
   * `<input type="date">` gives a bare "YYYY-MM-DD" — VesselFinder's
   * `/expectedarrivals` wants a full UTC stamp, "YYYY-MM-DD HH:mm:ss". Same
   * `moment` idiom `taskcost.component.ts` uses for `openWindow`/`closeWindow`
   * (`.utcOffset(0, true)` — relabels the picked wall-clock time as UTC
   * without shifting it, so the calendar day the user picked is never
   * shifted by the browser's own timezone) plus `.startOf('day')`/
   * `.endOf('day')` so the pair spans the whole day.
   */
  private toVesselFinderUtcStamp(dateOnly: string, endOfDay: boolean): string | undefined {
    const parseMoment: any = (moment as any).default ?? (moment as any);
    const m = parseMoment(dateOnly, 'YYYY-MM-DD', true);
    if (!m.isValid()) return undefined;
    return m
      .utcOffset(0, true)
      [endOfDay ? 'endOf' : 'startOf']('day')
      .format('YYYY-MM-DD HH:mm:ss');
  }

  /** Same act as `VfSearchControlComponent.pick()`: center the map and open the popup — never spends. */
  pick(r: VesselFinderVesselRow, event?: Event): void {
    event?.stopPropagation();
    const key = String(r.mmsi ?? r.imo ?? '');
    if (key) {
      this.selectedKey = key;
      this.vfCtrl.centerVessel(key);
    }
  }

  search(): void {
    this.error = this._validationError();
    if (this.error) return;
    if (!this.canSearch()) {
      this.error = 'Please fill all required fields (LOCODE, interval, from date, and to date).';
      return;
    }

    // Both ends of the TOI, in the order `vesselFinderSpendRefusal` asks them.
    // This form does its own worst-case credit arithmetic below rather than
    // going through that helper, so the window checks have to be repeated here
    // — a form that skipped them would be the one path to the vendor that does
    // not know when the shop is open.
    if (isVesselFinderToiNotStarted(this.sensor)) {
      this.popupService.openPopup(CollectionPopupComponent, VESSEL_FINDER_TOI_NOT_STARTED_MESSAGE, '', '40%');
      return;
    }

    if (hasVesselFinderToiFinished(this.sensor)) {
      this.popupService.openPopup(CollectionPopupComponent, VESSEL_FINDER_TOI_ENDED_MESSAGE, '', '40%');
      return;
    }

    const unitCost = actionCredits(this.sensor, 'expectedArrivals') || 5;
    const remaining = vesselFinderCreditsRemaining(this.sensor);
    const requestedLimit = this.requestedVessels;
    const worstCaseCost = requestedLimit * unitCost;

    if (remaining != null && remaining < worstCaseCost) {
      this.popupService.openPopup(
        CollectionPopupComponent,
        `Insufficient credits to perform this action. Expected Arrival requires ${unitCost} credits per vessel. Please specify a vessel limit based on your remaining credit balance to proceed with the Expected Arrival search.`,
        '',
        '40%',
      );
      return;
    }

    const { taskid, sensorId } = this.vfCtrl;
    if (!taskid || !sensorId) {
      this.error = 'This sensor is not attached to a task yet.';
      return;
    }

    const aisType = this.aisTypeRaw
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n));

    this.loading = true;
    this.api
      .getVesselFinderExpectedArrivals({
        taskid,
        sensorId,
        locode: this.locode.trim(),
        intervalMinutes: this.intervalMinutes ?? undefined,
        fromDate: this.toVesselFinderUtcStamp(this.fromDate, false),
        toDate: this.toVesselFinderUtcStamp(this.toDate, true),
        limit: this.limit ?? undefined,
        aisType: aisType.length ? aisType : undefined,
      })
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          applyVesselFinderCreditSpend(
            this.sensor,
            res,
            actionCredits(this.sensor, 'expectedArrivals'),
          );
          const data = res?.data;
          if (!data || !Array.isArray(data) || !data.length) {
            this.results = [];
            this.showingResults = false;
            this.vfCtrl.setSearchResults([]);
            this.error = 'No data found.';
            return;
          }
          this.results = data.map((v: any) => ({
            mmsi: v?.mmsi ?? null,
            imo: v?.imo ?? null,
            name: v?.name ?? null,
            timeUtc: v?.timestamp ?? null,
            timestamp: v?.timestamp ?? null,
            stale: false,
            kept: false,
            latitude: v?.latitude ?? null,
            longitude: v?.longitude ?? null,
            speed: v?.speed ?? null,
            course: v?.course ?? null,
            heading: v?.heading ?? null,
            destination: v?.destination ?? null,
            lastPort: v?.lastPort ?? null,
            eta: v?.eta ?? null,
            positionSource: v?.positionSource ?? null,
            callSign: v?.callSign ?? null,
            shipType: v?.shipType ?? null,
            typeCode: v?.typeCode ?? null,
            flag: v?.flag ?? null,
          }));
          this.showingResults = this.results.length > 0;
          this.vfCtrl.setSearchResults(this.results);
          // Back to the DEFAULTS, not to blank: the form is required-field
          // heavy, and emptying it after every search made the next one four
          // fields of re-typing. Only the port (and any AIS-type narrowing)
          // is the user's to restate.
          this.locode = '';
          this.intervalMinutes = VF_DEFAULT_INTERVAL_MINUTES;
          this.fromDate = vfTodayUtc();
          this.toDate = vfTodayUtc();
          this.limit = VF_DEFAULT_LIMIT;
          this.aisTypeRaw = '';
        },
        error: (err) => {
          this.loading = false;
          if (err?.status === 402 || err?.error?.code === 'INSUFFICIENT_API_CREDITS') {
            const msg =
              err?.error?.msg ||
              `Insufficient credits to perform this action. Expected Arrival requires ${unitCost} credits per vessel. Please specify a vessel limit based on your remaining credit balance to proceed with the Expected Arrival search.`;
            this.popupService.openPopup(CollectionPopupComponent, msg, '', '40%');
          } else {
            this.error = err?.error?.msg || 'Search failed. Please try again.';
          }
        },
      });
  }
}
